/* VR-Search-Recents_2026-08-12-001
 *
 * Adds a panel to the header search that shows while the field is empty:
 * the visitor's own recent searches (kept in this browser only) and a set
 * of category shortcuts.
 *
 * Deliberately additive. The theme's <predictive-search> element and its
 * results are never touched — this panel only occupies the same area while
 * predictive search has nothing to show, and hides the moment it does.
 *
 * The category shortcuts are read out of the theme's own nav drawer, so they
 * are always real links from the live menu. Nothing here invents content.
 */
(function () {
  'use strict';

  var STORE_KEY = 'vr:recent-searches';
  var MAX_ITEMS = 6;
  var MAX_CATEGORIES = 6;
  var MIN_TERM_LENGTH = 2;

  /* Storage ---------------------------------------------------------- */

  function readTerms() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return [];
      var list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];
      return list
        .filter(function (t) { return typeof t === 'string' && t.trim().length; })
        .slice(0, MAX_ITEMS);
    } catch (e) {
      // Private mode, blocked storage, corrupted value — degrade to no history.
      return [];
    }
  }

  function writeTerms(list) {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify(list.slice(0, MAX_ITEMS)));
    } catch (e) {
      /* nothing to do — the panel simply stays empty next time */
    }
  }

  function rememberTerm(term) {
    term = (term || '').trim().replace(/\s+/g, ' ');
    if (term.length < MIN_TERM_LENGTH) return;
    var lower = term.toLowerCase();
    var list = readTerms().filter(function (t) { return t.toLowerCase() !== lower; });
    list.unshift(term);
    writeTerms(list);
  }

  function forgetTerm(term) {
    var lower = term.toLowerCase();
    writeTerms(readTerms().filter(function (t) { return t.toLowerCase() !== lower; }));
  }

  /* Icons ------------------------------------------------------------- */

  function svg(pathMarkup, extraClass) {
    var el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    el.setAttribute('viewBox', '0 0 20 20');
    el.setAttribute('aria-hidden', 'true');
    el.setAttribute('focusable', 'false');
    el.setAttribute('class', 'vr-search-suggest__icon' + (extraClass ? ' ' + extraClass : ''));
    el.innerHTML = pathMarkup; // static markup only, never visitor input
    return el;
  }

  function clockIcon() {
    return svg(
      '<circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" stroke-width="1.5"/>' +
      '<path d="M10 5.75V10l2.75 1.75" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>'
    );
  }

  function tagIcon() {
    return svg(
      '<path d="M3.75 3.75h5.1l7.4 7.4-5.1 5.1-7.4-7.4z" fill="none" stroke="currentColor" ' +
      'stroke-width="1.5" stroke-linejoin="round"/>' +
      '<circle cx="6.9" cy="6.9" r="1.15" fill="currentColor"/>'
    );
  }

  function closeIcon() {
    return svg(
      '<path d="M6 6l8 8M14 6l-8 8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
      'vr-search-suggest__icon--x'
    );
  }

  /* Category shortcuts ------------------------------------------------ */

  /* Pulled from the rendered nav drawer so they track the real menu. */
  function collectCategories() {
    var seen = Object.create(null);
    var out = [];
    var links = document.querySelectorAll(
      '#menu-drawer .menu-drawer__inner-submenu a.menu-drawer__menu-item'
    );

    for (var i = 0; i < links.length && out.length < MAX_CATEGORIES; i++) {
      var link = links[i];
      var label = (link.textContent || '').trim().replace(/\s+/g, ' ');
      var href = link.getAttribute('href');
      if (!label || !href || href === '#') continue;
      if (/^view all$/i.test(label)) continue;
      var key = label.toLowerCase();
      if (seen[key]) continue;
      seen[key] = true;
      out.push({ label: label, href: href });
    }
    return out;
  }

  /* Panel ------------------------------------------------------------- */

  function SearchSuggest(modal) {
    this.modal = modal;
    this.form = modal.querySelector('form.search');
    this.input = modal.querySelector('.search__input');
    this.details = modal.querySelector('details');
    if (!this.form || !this.input || !this.details) return;

    this.panel = this.buildPanel();
    this.form.appendChild(this.panel);

    this.bind();
    this.render();
    this.sync();
  }

  SearchSuggest.prototype.buildPanel = function () {
    var panel = document.createElement('div');
    panel.className = 'vr-search-suggest';
    panel.hidden = true;

    this.recentGroup = document.createElement('div');
    this.recentGroup.className = 'vr-search-suggest__group';

    var head = document.createElement('div');
    head.className = 'vr-search-suggest__head';

    var recentHeading = document.createElement('h2');
    recentHeading.className = 'vr-search-suggest__heading';
    recentHeading.textContent = 'Recent searches';

    this.clearButton = document.createElement('button');
    this.clearButton.type = 'button';
    this.clearButton.className = 'vr-search-suggest__clear';
    this.clearButton.textContent = 'Clear';

    head.appendChild(recentHeading);
    head.appendChild(this.clearButton);

    this.recentList = document.createElement('ul');
    this.recentList.className = 'vr-search-suggest__list';
    this.recentList.setAttribute('role', 'list');

    this.recentGroup.appendChild(head);
    this.recentGroup.appendChild(this.recentList);

    this.categoryGroup = document.createElement('div');
    this.categoryGroup.className = 'vr-search-suggest__group';

    var categoryHeading = document.createElement('h2');
    categoryHeading.className = 'vr-search-suggest__heading';
    categoryHeading.textContent = 'Browse categories';

    this.categoryList = document.createElement('ul');
    this.categoryList.className = 'vr-search-suggest__list';
    this.categoryList.setAttribute('role', 'list');

    this.categoryGroup.appendChild(categoryHeading);
    this.categoryGroup.appendChild(this.categoryList);

    panel.appendChild(this.recentGroup);
    panel.appendChild(this.categoryGroup);
    return panel;
  };

  SearchSuggest.prototype.render = function () {
    var self = this;
    var terms = readTerms();

    this.recentList.textContent = '';
    this.recentGroup.hidden = terms.length === 0;

    terms.forEach(function (term) {
      var li = document.createElement('li');
      li.className = 'vr-search-suggest__row';

      var pick = document.createElement('button');
      pick.type = 'button';
      pick.className = 'vr-search-suggest__item';
      pick.appendChild(clockIcon());

      var label = document.createElement('span');
      label.className = 'vr-search-suggest__label';
      label.textContent = term; // textContent, never innerHTML
      pick.appendChild(label);

      pick.addEventListener('click', function () {
        self.input.value = term;
        self.input.focus();
        self.input.dispatchEvent(new Event('input', { bubbles: true }));
        self.sync();
      });

      var drop = document.createElement('button');
      drop.type = 'button';
      drop.className = 'vr-search-suggest__remove';
      drop.setAttribute('aria-label', 'Remove "' + term + '" from recent searches');
      drop.appendChild(closeIcon());
      drop.addEventListener('click', function (event) {
        event.stopPropagation();
        forgetTerm(term);
        self.render();
        self.sync();
      });

      li.appendChild(pick);
      li.appendChild(drop);
      self.recentList.appendChild(li);
    });

    if (!this.categoryList.childElementCount) {
      var categories = collectCategories();
      this.categoryGroup.hidden = categories.length === 0;
      categories.forEach(function (cat) {
        var li = document.createElement('li');
        li.className = 'vr-search-suggest__row';

        var link = document.createElement('a');
        link.className = 'vr-search-suggest__item';
        link.href = cat.href;
        link.appendChild(tagIcon());

        var label = document.createElement('span');
        label.className = 'vr-search-suggest__label';
        label.textContent = cat.label;
        link.appendChild(label);

        li.appendChild(link);
        self.categoryList.appendChild(li);
      });
    }
  };

  /* Visible only while the field is empty and the modal is open, so it
     never competes with the theme's predictive results. */
  SearchSuggest.prototype.sync = function () {
    var isOpen = this.details.hasAttribute('open');
    var isEmpty = !this.input.value.trim();
    var hasContent = !this.recentGroup.hidden || !this.categoryGroup.hidden;
    this.panel.hidden = !(isOpen && isEmpty && hasContent);
  };

  SearchSuggest.prototype.bind = function () {
    var self = this;

    this.input.addEventListener('input', function () { self.sync(); });
    this.input.addEventListener('focus', function () { self.sync(); });
    this.details.addEventListener('toggle', function () {
      self.render();
      self.sync();
    });

    this.clearButton.addEventListener('click', function () {
      writeTerms([]);
      self.render();
      self.sync();
    });

    this.form.addEventListener('submit', function () {
      rememberTerm(self.input.value);
    });

    /* Most visitors never submit — they tap a suggestion or a product.
       Record what was typed in those cases too. */
    this.form.addEventListener('click', function (event) {
      var link = event.target.closest && event.target.closest('a');
      if (!link) return;
      if (link.closest('.vr-search-suggest')) return;
      rememberTerm(self.input.value);
    });
  };

  /* Boot -------------------------------------------------------------- */

  function init() {
    var modals = document.querySelectorAll('details-modal.header__search');
    for (var i = 0; i < modals.length; i++) {
      if (modals[i].dataset.vrSuggest === 'on') continue;
      modals[i].dataset.vrSuggest = 'on';
      new SearchSuggest(modals[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The header is a section — re-attach after a theme editor re-render. */
  document.addEventListener('shopify:section:load', init);
})();
